// Copyright (c) Docugami, Inc. All rights reserved.

import { setEditTokenRange } from "hooks/chunkSelection";
import { DgmlInstancesSingleton } from "hooks/documentDgml";
import { MessageBusSingleton } from "messaging/MessageBus";
import { ChunkResizeEvent } from "messaging/messages/ChunkResizeEvent";
import { MessageId } from "messaging/MessageTypes";
import { ChunkInstance, TokenRange } from "networking/api";
import { ChunkResizedSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { Logger, LoggerCategory } from "./Logger";
import { TokenRangeUtils } from "./TokenRangeUtils";
import { createFolderId } from "components/file_management/file_helpers";

interface ChunkResizeCase {
    name: string;
    isCase: (original: ChunkInstance, edited: TokenRange) => boolean;
    execute: (original: ChunkInstance, edited: TokenRange) => Promise<ChunkInstance>;
}
export class ChunkResizing {
    static _resizeCases: ChunkResizeCase[] = [
        { name: "NOOP", isCase: ChunkResizing.isNoop, execute: ChunkResizing.executeNoop },
        { name: "PSEUDO HIGHLIGHT", isCase: ChunkResizing.isPseudoHighlight, execute: ChunkResizing.executePsuedoHighlight },
        { name: "HYPERLINE RESIZE", isCase: ChunkResizing.isHyperlineResize, execute: ChunkResizing.executeHyperlineResize },
        { name: "DEFAULT", isCase: () => true, execute: ChunkResizing.resize }
    ];

    /**
     * Commits user resizing of a chunk, takes appropriate store edits and returns the resulting
     * ChunkInstance
     */
    static async commitResize(original: ChunkInstance, edited: TokenRange): Promise<ChunkInstance> {
        for (const resizeCase of ChunkResizing._resizeCases) {
            if (resizeCase.isCase(original, edited)) {
                Logger.LogInformation(LoggerCategory.CanvasSelection, `Executing chunk resize case: ${resizeCase.name}`);
                return await resizeCase.execute(original, edited);
            }
        }

        throw new Error("there is an issue with the resize cases, default case not executed!");
    }

    static async resize(original: ChunkInstance, edited: TokenRange): Promise<ChunkInstance> {
        const existing = DgmlInstancesSingleton.getInstancesFlat(original.folderId)
            .find(x => TokenRangeUtils.rangesEqual(x.tokenRange!, edited));

        if (!!existing) {
            // There is an existing chunk with the target span id, so we can just return that
            ChunkResizing.notifyResize(original, existing);
            return existing;
        }

        const folderId = createFolderId(original.folderId);
        const mappedTokens = DgmlInstancesSingleton.getMappedTokens(original.folderId)!
        let instance: ChunkInstance = await DgmlInstancesSingleton.resize({
            docsetId: folderId.clusterId,
            fileId: folderId.fileId,
            spanId: original.spanId!,
            tokenKeyRange: mappedTokens.tokenRangeToTokenKeyRange(edited),
            dgmlVersion: DgmlInstancesSingleton.getDgmlVersion(original.folderId)!.value
        });

        ChunkResizing.notifyResize(original, instance);
        return instance;
    }

    /**
     * Simple case - this wasn't actually resized
     */
    static isNoop(original: ChunkInstance, edited: TokenRange): boolean {
        return TokenRangeUtils.rangesEqual(original.tokenRange!, edited);
    }

    static async executeNoop(original: ChunkInstance, edited: TokenRange): Promise<ChunkInstance> {
        return original;
    }

    /**
     * The Pseudo Highlight - this wasn't a resize, it was stealing an existing chunk
     * to select something else!
     */
    static isPseudoHighlight(original: ChunkInstance, edited: TokenRange): boolean {
        return !TokenRangeUtils.rangesOverlap(original.tokenRange!, edited);
    }

    static async executePsuedoHighlight(original: ChunkInstance, edited: TokenRange): Promise<ChunkInstance> {
        // is there a chunk with this range already?
        const folderId = original.folderId;
        const existing = DgmlInstancesSingleton.getInstancesFlat(folderId)
            .find(x => TokenRangeUtils.rangesEqual(x.tokenRange!, edited));

        if (!!existing) {
            ChunkResizing.notifyResize(original, existing);
            return existing;
        }

        const newInstance = await ChunkResizing.createNewChunk(original, edited, false);
        return newInstance;
    }

    /**
     * The hyperline growth.  Hyperlines are atomic - if you are resizing one of these then
     * we need to create a new chunk instead of resizing the hyperline itself
     */
    static isHyperlineResize(original: ChunkInstance, edited: TokenRange): boolean {
        return DgmlInstancesSingleton.isNonResizableChunk(original);
    }

    static async executeHyperlineResize(original: ChunkInstance, edited: TokenRange): Promise<ChunkInstance> {
        // is there a chunk with this range already?
        const folderId = original.folderId;
        const existing = DgmlInstancesSingleton.getInstancesFlat(folderId)
            .find(x => TokenRangeUtils.rangesEqual(x.tokenRange!, edited));

        if (!!existing) {
            ChunkResizing.notifyResize(original, existing);
            return existing;
        }

        const label = original.chunk?.labels[0];
        let useOriginalLabel = false;
        if (label?.toolId === 'cp') {
            // We assume you don't want the same semantic label since you're resizing the original
            const fid = createFolderId(folderId);
            await DgmlInstancesSingleton.removeLabel({
                docsetId: fid.clusterId,
                fileId: fid.fileId,
                spanId: original.spanId!,
                label: label.value
            });

            useOriginalLabel = true;
        }

        const newInstance = await ChunkResizing.createNewChunk(original, edited, useOriginalLabel);
        ChunkResizing.notifyResize(original, newInstance);
        return newInstance;
    }

    private static async createNewChunk(original: ChunkInstance, edited: TokenRange, useOriginalLabel: boolean): Promise<ChunkInstance> {
        const folderId = createFolderId(original.folderId);
        return await DgmlInstancesSingleton.label({
            docsetId: folderId.clusterId,
            fileId: folderId.fileId,
            spanId: DgmlInstancesSingleton.getSpanIdForTokenRange(original.folderId, edited),
            label: useOriginalLabel ? original?.chunk?.labels[0]?.value : undefined,
            dgmlVersion: DgmlInstancesSingleton.getDgmlVersion(original.folderId)!.value
        });
    }

    private static notifyResize(original: ChunkInstance, edited: ChunkInstance) {
        setEditTokenRange();

        const chunkResizeEvent: ChunkResizeEvent = {
            id: MessageId.ChunkResizeEvent,
            original: original,
            result: edited
        };

        MessageBusSingleton.sendMessage(chunkResizeEvent);

        Telemetry.trackEvent<ChunkResizedSchema>({
            originalChunkInstanceId: original.chunkInstanceId ?? '',
            originalSpanId: original.spanId ?? '',
            newChunkInstanceId: edited.chunkInstanceId ?? '',
            newSpanId: edited.spanId ?? '',
            eventName: TelemetryEvent.ChunkResized
        });
    }
}
